import type {
  FixMessage,
  FixMessageTag,
  FixTag,
  ParseError,
  ParseResult,
} from "../domain";
import {
  DEFAULT_FIX_VERSION,
  getDataFieldLengthTag,
  isDataField,
  resolveEnumValue,
  resolveTagDescription,
  resolveTagId,
  resolveTagKind,
  resolveTagName,
} from "./thesaurus";
import { ErrorCodes } from "./error";
import { isPositiveInteger } from "./number";
import { detectFixMessageSeparator } from "./parser-sep-detector";
import { splitByFixMessage } from "./parser-splitter";

const findLengthForDataField = (
  tags: FixTag[],
  dataTagId: number,
): number | null => {
  const lengthTag = getDataFieldLengthTag(dataTagId);
  if (lengthTag === undefined) return null;
  for (let i = tags.length - 1; i >= 0; i--) {
    if (tags[i].key === lengthTag) {
      const raw = tags[i].value;
      if (raw === null || !isPositiveInteger(raw)) return null;
      return Number.parseInt(raw, 10);
    }
  }
  return null;
};

export const parseFixTagsFromLine = (
  line: string,
  separator: string,
): ParseResult<FixTag[]> => {
  const fixTags: FixTag[] = [];
  const errors: ParseError[] = [];

  let pos = 0;
  while (pos < line.length) {
    // Skip leading separators (handles repeated/leading delimiters).
    while (pos < line.length && line[pos] === separator) pos++;
    if (pos >= line.length) break;

    // Find the field terminator (next separator) and the key/value boundary.
    let sepPos = line.indexOf(separator, pos);
    if (sepPos === -1) sepPos = line.length;
    const part = line.substring(pos, sepPos);

    if (part.length === 0) {
      pos = sepPos + 1;
      continue;
    }

    const delimiterPos = part.indexOf("=");
    if (delimiterPos === -1) {
      errors.push({
        code: ErrorCodes.FIX_TAG_VALUE_NOT_FOUND,
        message: `Value not found in "${part}" because "=" as delimiter not found`,
      });
      if (!isPositiveInteger(part)) {
        errors.push({
          code: ErrorCodes.FIX_TAG_KEY_IS_NOT_NUMBER,
          message: `Tag "${part}" is not a integer number`,
        });
        pos = sepPos + 1;
        continue;
      }
      fixTags.push({ key: Number.parseInt(part), value: null });
      pos = sepPos + 1;
      continue;
    }

    const tagIdStr = part.substring(0, delimiterPos);
    if (!isPositiveInteger(tagIdStr)) {
      errors.push({
        code: ErrorCodes.FIX_TAG_KEY_IS_NOT_NUMBER,
        message: `Tag "${tagIdStr}" is not a integer number in "${part}"`,
      });
      pos = sepPos + 1;
      continue;
    }

    const tagId = Number.parseInt(tagIdStr);
    const valueStart = pos + delimiterPos + 1;

    // Length-prefixed data field: consume exactly N characters after the "="
    // even if they include embedded separators.
    if (isDataField(tagId)) {
      const dataLength = findLengthForDataField(fixTags, tagId);
      if (dataLength === null) {
        const lengthTag = getDataFieldLengthTag(tagId);
        errors.push({
          code: ErrorCodes.DATA_FIELD_LENGTH_MISSING,
          message: `Did not find length field ${lengthTag} required to parse data field ${tagId}`,
        });
        // Fall back to separator-based read (sepPos already set).
        fixTags.push({ key: tagId, value: line.substring(valueStart, sepPos) });
        pos = sepPos + 1;
        continue;
      }

      const valueEnd = valueStart + dataLength;
      if (valueEnd > line.length) {
        // Truncated; take what's left and report via the separator-based fallback.
        fixTags.push({
          key: tagId,
          value: line.substring(valueStart),
        });
        pos = line.length;
        continue;
      }
      fixTags.push({
        key: tagId,
        value: line.substring(valueStart, valueEnd),
      });
      // Advance past the value and its trailing separator (if any).
      pos = valueEnd;
      if (pos < line.length && line[pos] === separator) pos++;
      continue;
    }

    fixTags.push({
      key: tagId,
      value: line.substring(valueStart, sepPos),
    });
    pos = sepPos + 1;
  }

  return { data: fixTags, errors };
};

export const populateFixTag = (
  tag: FixTag,
  protocolVersion?: string,
): FixMessageTag => {
  return {
    tag: tag.key,
    tagDescription: resolveTagName(tag.key, protocolVersion),
    value: tag.value,
    tagDescriptionExplain: resolveTagDescription(tag.key, protocolVersion),
    valueDescription: resolveEnumValue(tag.key, tag.value, protocolVersion),
    kind: resolveTagKind(tag.key),
  };
};

export type ShortTagsIndex = Record<number, string>;

export const createShortIndex = (
  tags: FixTag[],
  scan: number[],
): ShortTagsIndex => {
  const scanIndex = new Set(scan);
  const index: ShortTagsIndex = {};
  for (const tag of tags) {
    if (scanIndex.has(tag.key)) {
      index[tag.key] = tag.value != null ? tag.value : "";
    }
  }
  for (const tagId of scan) {
    if (index[tagId] === undefined) {
      index[tagId] = "";
    }
  }

  return index;
};

export const populateFixMessage = (tags: FixTag[]): FixMessage => {
  const protocolVersion =
    tags.find((t) => t.key === resolveTagId("BeginString"))?.value ||
    DEFAULT_FIX_VERSION;

  const mainTags = createShortIndex(tags, [
    resolveTagId("ClOrdID"),
    resolveTagId("SenderCompID"),
    resolveTagId("TargetCompID"),
    resolveTagId("SendingTime"),
    resolveTagId("MsgType"),
  ]);

  return {
    time: mainTags[resolveTagId("SendingTime")],
    sender: mainTags[resolveTagId("SenderCompID")],
    target: mainTags[resolveTagId("TargetCompID")],
    messageType: mainTags[resolveTagId("MsgType")],
    messageTypeDescription: resolveEnumValue(
      resolveTagId("MsgType"),
      mainTags[resolveTagId("MsgType")],
    ),
    message: tags.map((kv) => `${kv.key}=${kv.value}`).join("|"),
    clorId: mainTags[resolveTagId("ClOrdID")],

    tags: tags.map((t) => populateFixTag(t, protocolVersion)),
  };
};

export const evaluateChecksum = (fixTags: FixTag[] | undefined): number => {
  if (fixTags === undefined) {
    return 0;
  }

  const str =
    fixTags
      .map((t) => `${t.key}=${t.value}`)
      .slice(0, fixTags.length - 1)
      .join("\x01") + "\x01";

  let checkSum = 0;
  for (let i = 0; i < str.length; i++) {
    checkSum += str.charCodeAt(i);
  }

  return checkSum % 256;
};

export const injectChecksumMatching = (
  messageParts: ParseResult<FixTag[]>,
): void => {
  const checkSumTag = resolveTagId("CheckSum");
  const expectedCheckSum =
    messageParts.data &&
    messageParts.data.length > 0 &&
    messageParts.data[messageParts.data.length - 1].key === checkSumTag
      ? messageParts.data[messageParts.data.length - 1]
      : null;
  if (!expectedCheckSum) {
    messageParts.errors.push({
      code: ErrorCodes.INVALID_CHECKSUM,
      message: `CheckSum[${checkSumTag}] is not found as last tag in message`,
    });
  } else if (
    expectedCheckSum.value === null ||
    !isPositiveInteger(expectedCheckSum.value)
  ) {
    messageParts.errors.push({
      code: ErrorCodes.INVALID_CHECKSUM,
      message: `CheckSum[${checkSumTag}] should be integer but found "${expectedCheckSum.value}"`,
    });
  } else {
    const actualCheckSum = evaluateChecksum(messageParts.data);
    const expectedCheckSumValue = parseInt(expectedCheckSum.value.toString());
    if (actualCheckSum !== expectedCheckSumValue) {
      messageParts.errors.push({
        code: ErrorCodes.INVALID_CHECKSUM,
        message: `CheckSum[${checkSumTag}]="${expectedCheckSumValue}" not equal to actual checksum "${actualCheckSum}"`,
      });
    }
  }
};

const FIX_BEGIN_MARKER = "8=FIX";

export const parseFixMessage = (line: string): ParseResult<FixMessage> => {
  const firstIndex = line.indexOf(FIX_BEGIN_MARKER);
  if (firstIndex === -1) {
    return {
      source: line,
      errors: [
        {
          code: ErrorCodes.NO_BEGIN_MARKER,
          message: "Begin market 8=FIX not found",
        },
      ],
    };
  }

  const separator = detectFixMessageSeparator(line.substring(firstIndex));
  if (separator == null) {
    return {
      source: line,
      errors: [
        {
          code: ErrorCodes.FIX_FIELDS_SEPARATOR_NOT_FOUND,
          message: "Separator that most likely is | or 0x01 not found",
        },
      ],
    };
  }

  const messageParts: ParseResult<FixTag[]> = parseFixTagsFromLine(
    line.substring(firstIndex),
    separator,
  );
  injectChecksumMatching(messageParts);

  return {
    source: line,

    data: populateFixMessage(messageParts.data ? messageParts.data : []),
    errors: messageParts.errors,
  };
};

export const containsMultipleFixMessagesInLine = (line: string) => {
  const firstPos = line.indexOf(FIX_BEGIN_MARKER);
  return firstPos !== -1 && line.indexOf(FIX_BEGIN_MARKER, firstPos + 1) > -1;
};

const parseAsSingleLine = (
  line: string,
  messages: ParseResult<FixMessage>[],
) => {
  const message = parseFixMessage(line);
  if (message != null) {
    messages.push(message);
  }
};

export const parseAsMultiLine = (
  line: string,
  messages: ParseResult<FixMessage>[],
) => {
  const parts = splitByFixMessage(line);
  for (const part of parts) {
    if (part === "") {
      continue;
    }
    const message = parseFixMessage(part);
    if (message != null) {
      messages.push(message);
    }
  }
};

export const parseFixMessages = (data: string): ParseResult<FixMessage>[] => {
  if (data.length === 0) return [];

  const lines: string[] = data.split("\n");
  const messages: ParseResult<FixMessage>[] = [];
  for (const line of lines) {
    if (line.length === 0) continue;

    if (containsMultipleFixMessagesInLine(line)) {
      parseAsMultiLine(line, messages);
    } else {
      parseAsSingleLine(line, messages);
    }
  }

  return messages;
};
